import React from "react";
import { Box, Typography, Divider, Skeleton, Popover } from "@mui/material";
import { T } from "../../../hooks/useTranslate";

const PvBreakdownPopover = ({ anchorEl, onClose, status, data, onRowClick }) => {
  const open = Boolean(anchorEl);

  const items = Array.isArray(data?.data) ? data.data : [];
  // GO-3359: keep RAW values through the pipeline; round only at render.
  // Each row displays Math.round(raw), but the TOTAL is round(sum(raw)) —
  // never a sum of per-row rounded values, which loses precision (three rows
  // of 0.4000 rendered 0/0/0 with a total of 0 against a true 1.2). Rows can
  // read 0,0,0 while the total reads 1: correct and intended. feelgreat-biz-rn's
  // PvBreakdownModal applies the identical rule (fix both together or neither).
  const parsedItems = items.map((item) => {
    const rawValue = parseFloat(item.Value) || 0;
    return {
      ...item,
      rawValue,
      parsedValue: Math.round(rawValue),
    };
  });
  const total = Math.round(
    parsedItems.reduce((sum, item) => sum + item.rawValue, 0)
  );

  const isLoading = status === 'loading' || status === 'uninitialized' || status === 'wait';

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      keepMounted
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      PaperProps={{
        sx: {
          width: '225px',
          borderRadius: '8px',
          border: '0.5px solid #EFEFEF',
          boxShadow: '0 4px 8px 0 rgba(224, 224, 224, 0.5)',
          p: '12px',
          mt: '4px',
          ml: '-24px',
        },
      }}
    >
      {/* Header row */}
      <Box data-test-id="pv-breakdown-header" display="flex" justifyContent="space-between" mb="6px">
        <Typography sx={{ color: 'var(--Foundation-Grey-grey-800, #50575E)', fontFamily: 'Inter', fontSize: '12px', fontWeight: 400, lineHeight: 'normal' }}><T>name</T></Typography>
        <Typography sx={{ color: 'var(--Foundation-Grey-grey-800, #50575E)', fontFamily: 'Inter', fontSize: '12px', fontWeight: 400, lineHeight: 'normal' }}><T>volume</T></Typography>
      </Box>

      <Divider />

      {/* Loading state */}
      {isLoading && (
        <Box mt="6px">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="text" height={28} sx={{ mb: '4px' }} />
          ))}
        </Box>
      )}

      {/* Data rows */}
      {!isLoading && parsedItems.map((item, idx) => (
        <Box
          key={idx}
          data-test-id="pv-breakdown-row"
          display="flex"
          justifyContent="space-between"
          mt="6px"
          onClick={onRowClick ? () => onRowClick(item) : undefined}
          sx={onRowClick ? { cursor: 'pointer' } : undefined}
        >
          <Typography sx={{ color: 'var(--Foundation-Grey-grey-800, #50575E)', fontFamily: 'Inter', fontSize: '12px', fontWeight: 500, lineHeight: 'normal' }}>{item.FromName}</Typography>
          <Typography sx={{ color: 'var(--Black, #000)', fontFamily: 'Inter', fontSize: '12px', fontWeight: 500, lineHeight: 'normal', textAlign: 'right' }}>{item.parsedValue}</Typography>
        </Box>
      ))}

      {/* Total row */}
      {!isLoading && parsedItems.length > 0 && (
        <>
          <Divider sx={{ mt: '6px' }} />
          <Box data-test-id="pv-breakdown-total" display="flex" justifyContent="space-between" mt="6px">
            <Typography sx={{ color: 'var(--Foundation-Grey-grey-800, #50575E)', fontFamily: 'Inter', fontSize: '12px', fontWeight: 400, lineHeight: 'normal' }}><T>total_pv</T></Typography>
            <Typography sx={{ color: 'var(--Black, #000)', fontFamily: 'Inter', fontSize: '12px', fontWeight: 700, lineHeight: 'normal', textAlign: 'right' }}>{total}</Typography>
          </Box>
        </>
      )}
    </Popover>
  );
};

export { PvBreakdownPopover };
